import { action } from '@ember/object';
import { trackedMap } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismMessage,
  PrismModal,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralTablesCellsTdTwoLine from 'lumen/components/general/tables/cells/td-two-line';
import CellProductAccess from 'lumen/components/settings/users/tables/cells/cell-product-access';
import CellRole from 'lumen/components/settings/users/tables/cells/cell-role';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import {
  ACCESS_RIGHTS,
  type AccessRight,
} from 'lumen/utils/constants/users-constants';

interface PropertyAccessChange {
  accessRight?: string;
  accessibleProducts?: Record<string, boolean>;
  subscriptionIds: number[];
  userId: string;
}

interface ModalPropertyAccessSignature {
  Args: {
    onClose: () => void;
    onSave: (changes: PropertyAccessChange[]) => Promise<boolean>;
    relationships: Userhotelrelationship[];
    userEmail: string;
    userId: string;
  };
}

interface RowOverride {
  biBenchmarking?: boolean;
  rateAndMarketInsight?: boolean;
  role?: AccessRight;
}

const COLUMNS = {
  BI_BENCHMARKING: 'biBenchmarking',
  CREATED_AT: 'createdAt',
  PROPERTY_NAME: 'propertyName',
  RATE_AND_MARKET_INSIGHT: 'rateAndMarketInsight',
  ROLE: 'role',
} as const;

function toProductAccessState(
  hasAccess: boolean,
  isNotEditable: boolean,
): string {
  if (isNotEditable) {
    return hasAccess ? 'readonly' : 'unavailable';
  }
  return hasAccess ? 'checked' : 'unchecked';
}

export type { PropertyAccessChange };

export default class ModalPropertyAccess extends Component<ModalPropertyAccessSignature> {
  @service declare intl: Services['intl'];

  @tracked isSaving = false;
  private rowOverrides = trackedMap<number, RowOverride>();

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('useraccess_rights_header_dateadded'),
        valuePath: COLUMNS.CREATED_AT,
        textAlign: 'left' as const,
        width: 140,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.property'),
        valuePath: COLUMNS.PROPERTY_NAME,
        tdComponent: GeneralTablesCellsTdTwoLine,
        textAlign: 'left' as const,
        isSortable: false,
        width: 400,
      },
      {
        name: this.intl.t('settings.users.column.user_role'),
        valuePath: COLUMNS.ROLE,
        tdComponent: CellRole,
        textAlign: 'left' as const,
        width: 200,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.rate_insight_market_insight'),
        valuePath: COLUMNS.RATE_AND_MARKET_INSIGHT,
        tdComponent: CellProductAccess,
        textAlign: 'left' as const,
        isSortable: false,
        width: 200,
      },
      {
        name: this.intl.t('settings.users.column.bi_benchmarking'),
        valuePath: COLUMNS.BI_BENCHMARKING,
        tdComponent: CellProductAccess,
        textAlign: 'left' as const,
        isSortable: false,
      },
    ];
  }

  get hasPendingChanges(): boolean {
    return this.rowOverrides.size > 0;
  }

  get isSaveDisabled(): boolean {
    return this.isSaving || !this.hasPendingChanges;
  }

  get rows(): CustomEmberTableRow[] {
    return this.args.relationships.map((relationship) => {
      const {
        accessRight,
        createdAt,
        isLocked,
        name,
        products,
        subscriptionDescription,
        subscriptionId,
      } = relationship;
      const override = this.rowOverrides.get(subscriptionId);

      const originalRole = accessRight.toUpperCase() as AccessRight;
      const effectiveRole = override?.role ?? originalRole;
      const isAdmin = effectiveRole === ACCESS_RIGHTS.ADMIN;

      const rateInsightProduct = products?.rate_insight;
      const biAccessProduct = products?.revenue_insight;
      const hasRateInsight = rateInsightProduct !== undefined;
      const hasBiAccess = biAccessProduct !== undefined;

      const originalRateInsight = rateInsightProduct?.is_accessible ?? false;
      const originalBiAccess = biAccessProduct?.is_accessible ?? false;

      const effectiveRateInsight = isAdmin
        ? true
        : (override?.rateAndMarketInsight ?? originalRateInsight);
      const effectiveBiAccess = isAdmin
        ? true
        : (override?.biBenchmarking ?? originalBiAccess);

      const rateInsightEditable = rateInsightProduct?.is_editable ?? false;
      const biAccessEditable = biAccessProduct?.is_editable ?? false;

      const accessibleCount =
        (effectiveRateInsight ? 1 : 0) + (effectiveBiAccess ? 1 : 0);

      const isRateInsightNotEditable =
        !hasRateInsight ||
        isAdmin ||
        !rateInsightEditable ||
        (effectiveRateInsight && accessibleCount === 1);
      const isBiAccessNotEditable =
        !hasBiAccess ||
        isAdmin ||
        !biAccessEditable ||
        (effectiveBiAccess && accessibleCount === 1);

      return {
        [COLUMNS.BI_BENCHMARKING]: toProductAccessState(
          effectiveBiAccess,
          isLocked || isBiAccessNotEditable,
        ),
        [COLUMNS.CREATED_AT]: createdAt
          ? dayjs(createdAt).format('DD MMM YYYY')
          : this.intl.t('general.missing'),
        [COLUMNS.PROPERTY_NAME]: {
          primary: name,
          secondary: subscriptionDescription,
        },
        [COLUMNS.RATE_AND_MARKET_INSIGHT]: toProductAccessState(
          effectiveRateInsight,
          isLocked || isRateInsightNotEditable,
        ),
        [COLUMNS.ROLE]: effectiveRole,
        isDisabled: isLocked,
        isLocked,
        onProductChange: (valuePath: string, checked: boolean): void => {
          const key =
            valuePath === COLUMNS.RATE_AND_MARKET_INSIGHT
              ? 'rateAndMarketInsight'
              : 'biBenchmarking';
          this.updateRowOverride(
            subscriptionId,
            { [key]: checked },
            relationship,
          );
        },
        onRoleChange: (role: AccessRight): void => {
          this.updateRowOverride(subscriptionId, { role }, relationship);
        },
        originalRole,
        originalValues: {
          [COLUMNS.RATE_AND_MARKET_INSIGHT]: toProductAccessState(
            originalRateInsight,
            isLocked || isRateInsightNotEditable,
          ),
          [COLUMNS.BI_BENCHMARKING]: toProductAccessState(
            originalBiAccess,
            isLocked || isBiAccessNotEditable,
          ),
        },
      };
    });
  }

  private buildChanges(): PropertyAccessChange[] {
    const changes: PropertyAccessChange[] = [];

    for (const [subscriptionId, override] of this.rowOverrides) {
      const relationship = this.args.relationships.find(
        (relationship) => relationship.subscriptionId === subscriptionId,
      );
      if (!relationship) {
        continue;
      }

      const base = {
        subscriptionIds: [subscriptionId],
        userId: this.args.userId,
      };

      const originalRole =
        relationship.accessRight.toUpperCase() as AccessRight;

      if (override.role && override.role !== originalRole) {
        changes.push({
          ...base,
          accessRight: override.role.toLowerCase(),
        });
      }

      const effectiveRole = override.role ?? originalRole;
      const isAdmin = effectiveRole === ACCESS_RIGHTS.ADMIN;

      const originalRateInsight =
        relationship.products?.rate_insight?.is_accessible ?? false;
      const originalBiAccess =
        relationship.products?.revenue_insight?.is_accessible ?? false;

      const newRateInsight = isAdmin
        ? true
        : (override.rateAndMarketInsight ?? originalRateInsight);
      const newBiAccess = isAdmin
        ? true
        : (override.biBenchmarking ?? originalBiAccess);

      if (
        newRateInsight !== originalRateInsight ||
        newBiAccess !== originalBiAccess
      ) {
        changes.push({
          ...base,
          accessibleProducts: {
            rate_insight: newRateInsight,
            revenue_insight: newBiAccess,
          },
        });
      }
    }

    return changes;
  }

  private updateRowOverride(
    subscriptionId: number,
    update: Partial<RowOverride>,
    relationship: Userhotelrelationship,
  ): void {
    const existing = this.rowOverrides.get(subscriptionId);
    const override = { ...existing, ...update };

    const originalRole = relationship.accessRight.toUpperCase() as AccessRight;

    if (override.role === originalRole) {
      delete override.role;
    }

    const originalRateInsight =
      relationship.products?.rate_insight?.is_accessible ?? false;
    const originalBiAccess =
      relationship.products?.revenue_insight?.is_accessible ?? false;

    if (override.rateAndMarketInsight === originalRateInsight) {
      delete override.rateAndMarketInsight;
    }
    if (override.biBenchmarking === originalBiAccess) {
      delete override.biBenchmarking;
    }

    const effectiveRole = override.role ?? originalRole;
    if (effectiveRole === ACCESS_RIGHTS.ADMIN) {
      delete override.rateAndMarketInsight;
      delete override.biBenchmarking;
    }

    if (Object.keys(override).length > 0) {
      this.rowOverrides.set(subscriptionId, override);
    } else {
      this.rowOverrides.delete(subscriptionId);
    }
  }

  @action
  cancelAll(): void {
    this.rowOverrides.clear();
  }

  @action
  async saveAll(): Promise<void> {
    if (!this.hasPendingChanges) {
      return;
    }

    this.isSaving = true;

    try {
      const changes = this.buildChanges();
      const success = await this.args.onSave(changes);

      if (success) {
        this.rowOverrides.clear();
      }
    } finally {
      this.isSaving = false;
    }
  }

  <template>
    <PrismModal
      @onClose={{@onClose}}
      @title={{@userEmail}}
      data-test-id="modal-property-access"
    >
      <:default>
        <PrismTable
          @columns={{this.columns}}
          @isLoading={{false}}
          @rows={{this.rows}}
          @widthConstraint="gte-container-slack"
        >
          <:emptyState>
            <div class="flex h-full items-center justify-center p-600">
              <PrismMessage
                @illustration="100x100_search"
                @title={{t "settings.users.empty.nothing_matches_filters"}}
              />
            </div>
          </:emptyState>
        </PrismTable>
      </:default>

      <:footer>
        <div class="flex h-full items-center justify-end gap-200 px-500">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{if this.hasPendingChanges this.cancelAll @onClose}}
            data-test-id="modal-property-access-cancel"
          />
          <PrismButton
            @buttonType="primary"
            @isDisabled={{this.isSaveDisabled}}
            @isLoading={{this.isSaving}}
            @label={{t "general.save"}}
            @onClick={{this.saveAll}}
            data-test-id="modal-property-access-save"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
